import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { Icon, icons } from '../../generic';
import Card from '../../generic/Card/Card';
import Flex from '../../generic/Flex';
import Loader from '../../generic/Loader';
import { SidebarLayout } from '../../layouts';
import ModalStreamBuild from '../../modals/ModalStreamBuild';
import ProjectAffiliationContainer from '../generic/ProjectAffiliation';
import { ActionCards } from '../overview/StandardProjectSummaryActionCards';
import getRevisionStatus from '../shared/projectSummary/getRevisionStatus';
import type { ProjectSummaryProps } from '../projectDashboardTypes';

import IssueDataView from './IssueDataView';
import IssueScanDropdown from './IssueScanDropdown';
import SBOMAnalysisComponent from './SBOMAnalysis/SBOMAnalysisComponent';
import { ShareProjectModal } from './SBOMSharing';
import SBOMSummaryBuildProcess from './SBOMSummaryBuildProcess';
import useSummaryActions from './useSummaryActions';

import styles from './SBOMSummary.module.less';

type Props = Omit<ProjectSummaryProps, 'showPlanSelectorModal'>;

/**
 * Topline statistics and actions for SBOM projects
 */
const SBOMSummary: VoidFunctionComponent<Props> = ({
  project,
  revision,
  baseUrl,
  params,
  onProjectChange,
  onScanIssues,
}) => {
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);

  const {
    isRebuilding,
    onRebuild,
    rebuildBuildId,
    onShowNoDependenciesModal,
    onVisitAPIEndpoint,
    routerPush,
    onHideModalStreamBuild,
  } = useSummaryActions({ revision, params });

  const status = useMemo(() => getRevisionStatus(revision, project), [revision, project]);
  const isLoading = status === 'loading';
  const isBuildComplete = ['failing', 'analyzed'].includes(status);

  const onShareProject = () => {
    setIsShareModalVisible(true);
  };

  const onHideShareModal = () => {
    setIsShareModalVisible(false);
  };

  return (
    <div className={`container container-responsive ${styles.sbomSummary}`}>
      <SidebarLayout>
        <SidebarLayout.Main>
          <Card isFullWidth isContentFullHeight>
            <Card.Header>
              <div className={styles.header}>
                <div className={styles.headerStack}>
                  <span className={styles.summary}>Summary</span>
                  <span className={styles.revision}>Revision {revision.data?.loc?.revision}</span>
                </div>
                <div className={styles.buttons}>
                  <IssueScanDropdown revision={revision} />
                  {isBuildComplete && (
                    <div className="btn-group">
                      <Button type="button" className={styles.runNewScan} onClick={onScanIssues}>
                        <Icon icon={icons.reload} /> Run new scan
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card.Header>
            {isLoading && (
              <Card.Content>
                <Loader />
              </Card.Content>
            )}
            {!isLoading && (
              <Card.Content>
                {!isBuildComplete && (
                  <>
                    <SBOMSummaryBuildProcess
                      projectId={params.project_id}
                      revisionId={params.revision_id}
                      revision={revision}
                      onProjectChange={onProjectChange}
                    />
                    <SBOMAnalysisComponent
                      isBuildInProgress
                      projectLocator={params.project_id}
                      revisionId={params.revision_id}
                      revisionBaseUrl={baseUrl}
                      onRebuild={onRebuild}
                      isSBOMAnalysisEnabled={
                        (project.data.sbomAnalysisEnabled && _.isInteger(project.data.sbomPolicyId))
                      }
                    />
                  </>
                )}
                {isBuildComplete && (
                  <Flex gap={24} direction="column">
                    <IssueDataView project={project} revision={revision} params={params} />
                    <SBOMAnalysisComponent
                      projectLocator={params.project_id}
                      revisionId={params.revision_id}
                      revisionBaseUrl={baseUrl}
                      onRebuild={onRebuild}
                      isSBOMAnalysisEnabled={
                        (project.data.sbomAnalysisEnabled && _.isInteger(project.data.sbomPolicyId))
                      }
                    />
                  </Flex>
                )}
              </Card.Content>
            )}
          </Card>
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <ActionCards
            project={project}
            revision={revision}
            isScannedForIssues={false}
            revisionBaseUrl={baseUrl}
            isRebuilding={false}
            onRebuild={onRebuild}
            onShowNoDependenciesModal={onShowNoDependenciesModal}
            onVisitAPIEndpoint={onVisitAPIEndpoint}
            routerPush={routerPush}
            onShareProject={onShareProject}
          />
          <ProjectAffiliationContainer
            projectLocator={project.data.locator}
            labels={project.data.labels?.map((l) => l.label)}
          />
        </SidebarLayout.Sidebar>
      </SidebarLayout>
      {!project.data.require_mediated_dependencies && rebuildBuildId !== null && (
        <ModalStreamBuild
          key="rebuild-modal-stream-build"
          show={isRebuilding}
          buildId={rebuildBuildId}
          refId={params.reference}
          onHide={onHideModalStreamBuild}
          isProjectLocator
        />
      )}

      <ShareProjectModal
        isVisible={isShareModalVisible}
        onHide={onHideShareModal}
        projectLocator={project.data.locator as string}
        revisionLocator={revision.data?.locator}
        revisionUpdatedAt={revision.data?.updatedAt}
      />
    </div>
  );
};

export default SBOMSummary;
